/* ==========================================================================
   VAN LYNN — collection-filters.js
   AJAX storefront filtering & sorting (Shopify Section Rendering API),
   history support, "load more" pagination and the mobile filter drawer.
   Without JavaScript the filter form submits normally.
   ========================================================================== */
(function () {
  'use strict';
  if (customElements.get('collection-filters')) return;
  const VL = window.VL;

  class CollectionFilters extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.cache = new Map();
      this.onChange = VL.debounce(() => this.submitForm(), 450);

      this.addEventListener('change', (e) => {
        if (e.target.closest('[data-facets-form]') || e.target.matches('[data-sort-select]')) this.onChange();
      });
      this.addEventListener('submit', (e) => {
        if (!e.target.closest('[data-facets-form]')) return;
        e.preventDefault();
        this.submitForm();
      });
      this.addEventListener('click', (e) => {
        const remove = e.target.closest('[data-facet-remove]');
        if (remove) {
          e.preventDefault();
          this.render(new URL(remove.href, window.location.origin).searchParams.toString());
          return;
        }
        const more = e.target.closest('[data-load-more]');
        if (more) {
          e.preventDefault();
          this.loadMore(more);
          return;
        }
        if (e.target.closest('[data-facets-open]')) this.openDrawer(e.target.closest('[data-facets-open]'));
        if (e.target.closest('[data-facets-close]')) this.closeDrawer();
      });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.drawer?.classList.contains('is-open')) this.closeDrawer();
      });

      this.onPop = (e) => {
        if (e.state && e.state.vlFilters !== undefined) this.render(e.state.vlFilters, false);
      };
      window.addEventListener('popstate', this.onPop);
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPop);
    }

    get drawer() {
      return this.querySelector('[data-facets-drawer]');
    }

    buildParams() {
      const form = this.querySelector('[data-facets-form]');
      const params = new URLSearchParams();
      if (form) {
        new FormData(form).forEach((value, key) => {
          if (value !== '') params.append(key, value);
        });
      }
      const sort = this.querySelector('[data-sort-select]');
      if (sort) params.set('sort_by', sort.value);
      return params.toString();
    }

    submitForm() {
      this.render(this.buildParams());
    }

    async fetchSection(query) {
      const url = `${window.location.pathname}?section_id=${this.sectionId}${query ? `&${query}` : ''}`;
      if (this.cache.has(url)) return this.cache.get(url);
      const res = await fetch(url);
      if (!res.ok) throw new Error(res.status);
      const html = await res.text();
      this.cache.set(url, html);
      return html;
    }

    async render(query, push = true) {
      const results = this.querySelector('[data-results]');
      results?.classList.add('is-loading');
      this.setAttribute('aria-busy', 'true');
      try {
        const doc = VL.parseHTML(await this.fetchSection(query));
        const fresh = doc.querySelector('collection-filters');
        if (!fresh) throw new Error('missing section');

        ['[data-results]', '[data-active-filters]', '[data-results-count]', '[data-facets-open]'].forEach((sel) => {
          const a = this.querySelector(sel);
          const b = fresh.querySelector(sel);
          if (a && b) a.innerHTML = b.innerHTML;
        });

        // Update filter groups (counts, disabled states) but leave the one being edited untouched.
        const active = document.activeElement;
        fresh.querySelectorAll('[data-filter-group]').forEach((group) => {
          const current = this.querySelector(`#${CSS.escape(group.id)}`);
          if (!current || current.contains(active)) return;
          const wasOpen = current.open;
          current.innerHTML = group.innerHTML;
          current.open = wasOpen;
        });

        if (push) {
          const url = `${window.location.pathname}${query ? `?${query}` : ''}`;
          window.history.pushState({ vlFilters: query }, '', url);
        }
        const countEl = this.querySelector('[data-results-count]');
        if (countEl) VL.announce(countEl.textContent.trim());
        document.dispatchEvent(new CustomEvent('vl:content-loaded', { detail: { root: this } }));
      } catch (err) {
        VL.toast(window.theme.strings.searchError, 'error');
      } finally {
        this.querySelector('[data-results]')?.classList.remove('is-loading');
        this.removeAttribute('aria-busy');
      }
    }

    async loadMore(button) {
      button.classList.add('is-loading');
      try {
        const next = new URL(button.href, window.location.origin);
        next.searchParams.set('section_id', this.sectionId);
        const res = await fetch(next.toString());
        const doc = VL.parseHTML(await res.text());
        const list = this.querySelector('[data-product-list]');
        const items = doc.querySelectorAll('[data-product-list] > li');
        const firstNew = items[0];
        items.forEach((li) => list.appendChild(li));
        const wrap = this.querySelector('[data-load-more-wrap]');
        const freshWrap = doc.querySelector('[data-load-more-wrap]');
        if (wrap) wrap.innerHTML = freshWrap ? freshWrap.innerHTML : '';
        document.dispatchEvent(new CustomEvent('vl:content-loaded', { detail: { root: list } }));
        firstNew?.querySelector('a')?.focus({ preventScroll: true });
      } catch (err) {
        window.location.href = button.href;
      } finally {
        button.classList.remove('is-loading');
      }
    }

    openDrawer(trigger) {
      const drawer = this.drawer;
      if (!drawer) return;
      this.trigger = trigger;
      drawer.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');
      VL.lockScroll();
      VL.trapFocus(drawer.querySelector('.facets__panel'));
    }

    closeDrawer() {
      const drawer = this.drawer;
      if (!drawer || !drawer.classList.contains('is-open')) return;
      drawer.classList.remove('is-open');
      this.trigger?.setAttribute('aria-expanded', 'false');
      VL.unlockScroll();
      VL.removeTrapFocus(this.trigger);
    }
  }

  customElements.define('collection-filters', CollectionFilters);
})();
